import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Maintient l'écran allumé via l'API Screen Wake Lock.
 * Re-acquiert automatiquement le verrou si l'onglet redevient visible
 * (le navigateur libère le wake lock dès que la page passe en background).
 *
 * Support : Chrome/Edge (desktop+Android), Safari iOS 16.4+, Samsung Internet.
 */
const isSupported = () =>
  typeof navigator !== "undefined" && "wakeLock" in navigator;

export const useWakeLock = (initiallyEnabled = true) => {
  const [enabled, setEnabled] = useState(initiallyEnabled);
  const [active, setActive] = useState(false);
  const sentinelRef = useRef(null);
  const supported = isSupported();

  const request = useCallback(async () => {
    if (!supported) return;
    try {
      if (document.visibilityState !== "visible") return;
      if (sentinelRef.current) return;
      const sentinel = await navigator.wakeLock.request("screen");
      sentinelRef.current = sentinel;
      setActive(true);
      sentinel.addEventListener("release", () => {
        sentinelRef.current = null;
        setActive(false);
      });
    } catch (err) {
      // NotAllowedError when tab not visible, etc. Silent fail.
      setActive(false);
    }
  }, [supported]);

  const release = useCallback(async () => {
    try {
      if (sentinelRef.current) {
        await sentinelRef.current.release();
        sentinelRef.current = null;
      }
    } finally {
      setActive(false);
    }
  }, []);

  // Acquire/release based on `enabled`
  useEffect(() => {
    if (!supported) return;
    if (enabled) {
      request();
    } else {
      release();
    }
  }, [enabled, supported, request, release]);

  // Re-acquire when tab becomes visible again
  useEffect(() => {
    if (!supported) return;
    const onVis = () => {
      if (enabled && document.visibilityState === "visible") request();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [enabled, supported, request]);

  // Release on unmount
  useEffect(() => () => { release(); }, [release]);

  return { supported, enabled, active, setEnabled };
};

export default useWakeLock;
